import { UNLIMITED_AI_CREDITS_SENTINEL } from "@hex/common";
import React from "react";
import styled from "styled-components";

import { HexCleanLink, Txt } from "../../hex-components";
import { useCurrentUserIsAdmin } from "../../hooks/me/useCurrentUserIsAdmin";
import { Routes } from "../../route/routes";
import { ContactAnAdmin } from "../common/ContactAnAdmin";

import { formatCreditsForDisplay, getCreditUsagePercentage } from "./utils";

interface CreditExplanationProps {
  /** Whether the workspace has any quantity of add-on credits, consumed or not */
  addonsUsed: boolean;
  creditSpendLimitReached: boolean;
  enforcementDate: Date;
  topUpsEnabled: boolean;
  /** Whether the workspace has any available credits, not yet consumed */
  workspaceCreditsAvailable: boolean;
  userCreditsUsed: number;
  userCreditsTotal: number | typeof UNLIMITED_AI_CREDITS_SENTINEL;
}

export const CreditExplanation = React.memo(function CreditExplanation({
  addonsUsed,
  creditSpendLimitReached,
  enforcementDate,
  topUpsEnabled,
  userCreditsTotal,
  userCreditsUsed,
  workspaceCreditsAvailable,
}: CreditExplanationProps) {
  const isAdmin = useCurrentUserIsAdmin();
  const isPreEnforcement = new Date() < enforcementDate;

  const enforcementDateString = enforcementDate.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
  const userCreditsTotalString =
    userCreditsTotal === UNLIMITED_AI_CREDITS_SENTINEL
      ? "unlimited"
      : formatCreditsForDisplay(userCreditsTotal);

  if (isPreEnforcement) {
    if (topUpsEnabled || workspaceCreditsAvailable) {
      return (
        <div>
          Credit grants go into effect on{" "}
          <Txt fontWeight="medium">{enforcementDateString}</Txt>. Your seat
          includes <Txt fontWeight="medium">{userCreditsTotalString}</Txt> free
          credits per month, and you have access to add-on credits.{" "}
          {isAdmin && (
            <InlineLink to={Routes.SETTINGS.getUrl({ subView: "billing" })}>
              Manage credits
            </InlineLink>
          )}
        </div>
      );
    }

    // Else if top-ups are disabled and theres no credits available
    return isAdmin ? (
      <div>
        Credit grants go into effect on{" "}
        <Txt fontWeight="medium">{enforcementDateString}</Txt>. Your seat
        includes <Txt fontWeight="medium">{userCreditsTotalString}</Txt> free
        credits per month. Enable add-on credits in{" "}
        <InlineLink to={Routes.SETTINGS.getUrl({ subView: "billing" })}>
          Billing & credits
        </InlineLink>
        .
      </div>
    ) : (
      <div>
        Credit grants go into effect on{" "}
        <Txt fontWeight="medium">{enforcementDateString}</Txt>. Your seat
        includes <Txt fontWeight="medium">{userCreditsTotalString}</Txt> free
        credits per month. <ContactAnAdmin /> to request add-on credits.
      </div>
    );
  }

  // Begin post enforcement explanations
  const userFractionUsed = getCreditUsagePercentage(
    userCreditsTotal,
    userCreditsUsed,
  );

  const userLimitStatus =
    userFractionUsed < 1
      ? "under"
      : userFractionUsed >= 1 && addonsUsed
        ? "at_limit_with_workspace_credits"
        : "at_limit_no_workspace_credits";

  // If Top ups enabled AND spend limit reached AND workspace pool = 0
  if (topUpsEnabled && creditSpendLimitReached && !workspaceCreditsAvailable) {
    if (userLimitStatus === "under") {
      return isAdmin ? (
        <div>
          Your seat includes{" "}
          <Txt fontWeight="medium">{userCreditsTotalString}</Txt> free credits
          per month, but you&apos;re out of add-on credits.{" "}
          <InlineLink to={Routes.SETTINGS.getUrl({ subView: "billing" })}>
            Manage add-on credits
          </InlineLink>
          .
        </div>
      ) : (
        <div>
          Your seat includes{" "}
          <Txt fontWeight="medium">{userCreditsTotalString}</Txt> free credits
          per month. <ContactAnAdmin text="Contact your Admin" /> to request
          more add-on credits.
        </div>
      );
    } else if (
      userLimitStatus === "at_limit_no_workspace_credits" ||
      userLimitStatus === "at_limit_with_workspace_credits"
    ) {
      return isAdmin ? (
        <div>
          You&apos;ve used your monthly credit grant and you&apos;re out of
          add-on credits.{" "}
          <InlineLink to={Routes.SETTINGS.getUrl({ subView: "billing" })}>
            Manage add-on credits
          </InlineLink>
        </div>
      ) : (
        <div>
          You&apos;ve used your monthly credit grant.{" "}
          <ContactAnAdmin text="Contact your Admin" /> to request more add-on
          credits.
        </div>
      );
    }
  }

  // If (top ups enabled AND spend limit not reached) OR workspace pool > 0
  else if (
    (topUpsEnabled && !creditSpendLimitReached) ||
    workspaceCreditsAvailable
  ) {
    if (
      userLimitStatus === "under" ||
      userLimitStatus === "at_limit_no_workspace_credits"
    ) {
      return (
        <div>
          Your seat includes{" "}
          <Txt fontWeight="medium">{userCreditsTotalString}</Txt> free credits
          per month, and you have access to add-on credits.{" "}
          {isAdmin && (
            <InlineLink to={Routes.SETTINGS.getUrl({ subView: "billing" })}>
              Manage add-on credits
            </InlineLink>
          )}
        </div>
      );
    } else if (userLimitStatus === "at_limit_with_workspace_credits") {
      return (
        <div>
          You&apos;ve used your monthly credit grant and are now using add-on
          credits.{" "}
          {isAdmin && (
            <InlineLink to={Routes.SETTINGS.getUrl({ subView: "billing" })}>
              Manage credits
            </InlineLink>
          )}
        </div>
      );
    }
  }

  // If top ups not enabled AND workspace pool = 0
  else if (!topUpsEnabled && !workspaceCreditsAvailable) {
    if (userLimitStatus === "under") {
      return (
        <div>
          Your seat includes{" "}
          <Txt fontWeight="medium">{userCreditsTotalString}</Txt> free credits
          per month.{" "}
          {isAdmin ? (
            <>
              Enable add-on credits in{" "}
              <InlineLink to={Routes.SETTINGS.getUrl({ subView: "billing" })}>
                Billing & credits
              </InlineLink>
            </>
          ) : (
            <>
              <ContactAnAdmin text="Contact your Admin" /> to request add-on
              credits.
            </>
          )}
        </div>
      );
    } else if (
      userLimitStatus === "at_limit_no_workspace_credits" ||
      userLimitStatus === "at_limit_with_workspace_credits"
    ) {
      return (
        <div>
          You&apos;ve used your monthly credit grant.{" "}
          {isAdmin ? (
            <>
              Enable add-on credits in{" "}
              <InlineLink to={Routes.SETTINGS.getUrl({ subView: "billing" })}>
                Billing & credits
              </InlineLink>
              .
            </>
          ) : (
            <>
              <ContactAnAdmin text="Contact your Admin" /> to request add-on
              credits.
            </>
          )}
        </div>
      );
    }
  }

  return null;
});

const InlineLink = styled(HexCleanLink)`
  && {
    color: ${({ theme }) => theme.fontColor.LINK};
  }
`;
